import { useState } from 'react'
import { CheckCircle2, Circle, Clock, MinusCircle, Paperclip, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Modal, Button } from '@/components/ui'
import { openBlobInNewTab } from '@/utils/download'
import { cn } from '@/lib/cn'
import { getClaimErrorMessage, stepSummary } from '../utils/claimDisplay'
import type { ClaimReceipt, ClaimStepView } from '../types/expenseClaim.types'

/** Compact manager → admin progress, one line per step. */
export function ClaimSteps({ steps }: { steps: ClaimStepView[] }): React.ReactElement {
  return (
    <ul className="space-y-0.5">
      {steps.map((step) => {
        const Icon =
          step.status === 'approved'
            ? CheckCircle2
            : step.status === 'rejected'
              ? XCircle
              : step.status === 'skipped'
                ? MinusCircle
                : step.isCurrent
                  ? Clock
                  : Circle
        return (
          <li
            key={step.role}
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400"
            title={step.reason ?? step.note}
          >
            <Icon
              className={cn(
                'h-3.5 w-3.5 shrink-0',
                step.status === 'approved' && 'text-green-600',
                step.status === 'rejected' && 'text-red-600',
                step.status === 'pending' && step.isCurrent && 'text-amber-600'
              )}
              aria-hidden
            />
            <span>{stepSummary(step)}</span>
          </li>
        )
      })}
    </ul>
  )
}

/** Receipt links that fetch through the authenticated endpoint and open in a new tab. */
export function ClaimReceipts({
  receipts,
  download,
}: {
  receipts: ClaimReceipt[]
  download: (uploadId: string) => Promise<Blob>
}): React.ReactElement {
  const [openingId, setOpeningId] = useState<string | null>(null)
  if (receipts.length === 0) {
    return <span className="text-xs text-gray-400">No receipts</span>
  }
  const open = async (id: string): Promise<void> => {
    setOpeningId(id)
    try {
      openBlobInNewTab(await download(id))
    } catch (error) {
      toast.error(getClaimErrorMessage(error))
    } finally {
      setOpeningId(null)
    }
  }
  return (
    <ul className="space-y-0.5">
      {receipts.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => void open(r.id)}
            disabled={openingId !== null}
            className="inline-flex max-w-48 items-center gap-1 truncate text-xs text-primary hover:underline disabled:opacity-50"
          >
            <Paperclip className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">
              {openingId === r.id ? 'Opening…' : r.originalName || 'Receipt'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

interface ClaimNoteModalProps {
  open: boolean
  onClose: () => void
  title: string
  description: string
  label: string
  confirmLabel: string
  /** 0 means the note is optional. */
  minLength: number
  variant?: 'primary' | 'danger'
  loading?: boolean
  onConfirm: (note: string) => void
}

/** Approve (optional note), reject (reason) and mark-reimbursed (note) all ask one question. */
export function ClaimNoteModal(props: ClaimNoteModalProps): React.ReactElement | null {
  // Mounted only while open, so the note starts empty each time.
  return props.open ? <ClaimNoteBody {...props} /> : null
}

function ClaimNoteBody({
  open,
  onClose,
  title,
  description,
  label,
  confirmLabel,
  minLength,
  variant = 'primary',
  loading,
  onConfirm,
}: ClaimNoteModalProps): React.ReactElement {
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const confirm = (): void => {
    const trimmed = note.trim()
    if (trimmed.length < minLength) {
      setError(`Please write at least ${minLength} characters`)
      return
    }
    onConfirm(trimmed)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={confirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
        <label
          htmlFor="claim-note"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {minLength > 0 ? ' *' : ''}
        </label>
        <textarea
          id="claim-note"
          rows={3}
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  )
}
